// Reach relay-sdk's runtime exports through a namespace import, not named
// imports. Some integrator bundlers fail to bind individual named exports from
// relay-sdk's CJS build (dydx hit `convertViemChainToRelayChain is not a
// function`); a namespace read resolves off the live module object and is
// robust to that CJS/ESM interop gap.
import * as relaySdk from '@relayprotocol/relay-sdk'
import type {
  RelayClient,
  RelayClientOptions,
  log,
} from '@relayprotocol/relay-sdk'
import type { Chain } from 'viem'
import {
  RELAY_LIGHTER_CHAIN_ID_NUMBER,
  RELAY_SOLANA_CHAIN_ID_NUMBER,
} from './constants'
import type { Logger } from './types'

export type InitializeRelayClientParams = Omit<
  RelayClientOptions,
  'baseApiUrl' | 'chains' | 'logger'
> & {
  chains: Chain[]
  logger: Logger
}

/**
 * Initializes a global instance of the RelayClient
 * https://docs.relay.link/references/sdk/createClient
 */
export function initializeRelayClient({
  chains,
  logger,
  ...options
}: InitializeRelayClientParams): RelayClient {
  const relayLogger = (
    message: Parameters<typeof log>[0],
    level: relaySdk.LogLevel,
  ) => {
    const logType: keyof Logger =
      level === relaySdk.LogLevel.Error ? 'error' : 'info'
    const logMessage = message[0] ?? 'Unknown error'
    const logData = message.length > 1 ? message.slice(1) : undefined

    logger[logType]('[RelayClient]', {
      message: logMessage,
      relayLogLevel: level,
      ...(logData && { data: logData }),
    })
  }

  // relay-sdk v6 accepts the logger in createClient() directly, so the prior
  // post-creation configure() workaround (needed up to v5) is gone.
  return relaySdk.createClient({
    ...options,
    baseApiUrl: relaySdk.MAINNET_RELAY_API,
    logLevel: options.logLevel ?? relaySdk.LogLevel.Verbose,
    logger: relayLogger,
    chains: [
      ...chains.map(relaySdk.convertViemChainToRelayChain),
      {
        id: RELAY_SOLANA_CHAIN_ID_NUMBER,
        name: 'Solana',
        displayName: 'Solana',
      },
      {
        id: RELAY_LIGHTER_CHAIN_ID_NUMBER,
        name: 'Lighter',
        displayName: 'Lighter',
      },
    ],
  })
}

/**
 * Gets the global instance of the RelayClient
 */
export function getRelayClient(): RelayClient {
  const client = relaySdk.getClient()

  if (!client) {
    throw new Error('Relay client is not defined')
  }

  return client
}
